import type { LucideIcon } from 'lucide-react';
import {
  FlaskConical,
  BarChart3,
  Building2,
  Clock,
  FileSignature,
  History,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  Receipt,
  Settings,
  Users,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Shown only to org owners and admins. The SERVER decides (`GET
   * /activity/access`) and refuses everyone else on the route itself; hiding
   * the item just spares them a door that would say no.
   */
  adminOnly?: boolean;
}

/** Section-grouped nav. Feature agents do not edit this — scaffold owns it. */
export const NAV_SECTIONS: Array<{ label?: string; items: NavItem[] }> = [
  {
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Work',
    items: [
      { to: '/clients', label: 'Clients', icon: Building2 },
      { to: '/agreements', label: 'Agreements', icon: FileSignature },
      { to: '/time', label: 'Time', icon: Clock },
      { to: '/team', label: 'Team', icon: Users },
    ],
  },
  {
    label: 'Money',
    items: [
      { to: '/invoices', label: 'Invoices', icon: Receipt },
      // ONE forward-looking money destination (Jesse, 2026-08-12): the
      // half-month cash report, the Projected reality/potential list, and
      // "Pull from sales" as an action inside it. Cash forecast · Forecast ·
      // Sales pipeline were three items for one job against a workbook that is
      // one file with tabs.
      // Budgets was the fourth item here and is gone with its screen (Jesse:
      // "it's more about cash flow than it is staying within budget … I would
      // get rid of it"). MONEY is three: Invoices · Expenses · Budget &
      // Forecast — down from six before Wave H.
    ],
  },
  {
    label: 'Reports',
    items: [
      // ⚠ Budget & Forecast lives under REPORTS, not MONEY (Jesse, 2026-08-18:
      // *"Budget & Forecast is better suited in the Reports section"*).
      //
      // It is the one item here you TYPE into — the reconciled actual balance,
      // the expense plan, the deal checklist — so "Reports" is a slight stretch
      // on the label. It earns the place anyway: what people open it for is the
      // shape of the year, and it sits beside the other two screens read for
      // the same reason. MONEY is now the two ledgers money moves through.
      { to: '/budget-forecast', label: 'Budget & Forecast', icon: Landmark },
      { to: '/reports/pnl', label: 'Profit & Loss', icon: BarChart3 },
      { to: '/support', label: 'Support', icon: LifeBuoy },
      // Owners and admins only (Jesse, 2026-10-07): research time and its
      // cost for the R&D tax credit — it prints what people are paid.
      { to: '/reports/rd', label: 'R&D', icon: FlaskConical, adminOnly: true },
    ],
  },
  {
    items: [
      // Owners and admins only (Jesse, 2026-10-01): sends, receipts,
      // payments and time, as they happened.
      { to: '/activity', label: 'Activity', icon: History, adminOnly: true },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap((s) => s.items);

/**
 * Routes that are real screens but deliberately not nav items — reached only
 * from the screen they belong to. They still need a name: a page with no entry
 * here printed "Solid Financials" in the topbar, which is what a page with no
 * identity looks like. A new route with no nav entry MUST be listed here.
 *
 * The connection screens are the entries today: BOTH Settings → Integrations
 * (third-party systems) and Settings → Hub (the Solid Digital ecosystem) are
 * indexes of rows, and each button opens that connection's own screen (Jesse,
 * 2026-08-13). Every one is linked from its index and offers a back link to it. /settings/harvest-sync is a redirect into the Harvest screen, and
 * /cashflow, /forecast and /sales-pipeline are redirects into /budget-forecast
 * — a redirect is not a screen and needs no title.
 */
const OFF_NAV_TITLES: Array<[prefix: string, label: string]> = [
  ['/settings/integrations/harvest', 'Harvest'],
  ['/settings/integrations/stripe', 'Stripe'],
  ['/settings/integrations/quickbooks', 'QuickBooks Online'],
  ['/settings/integrations/plaid', 'Plaid'],
  ['/invoices/deposits', 'Bank deposits'],
  ['/settings/ecosystem/hub', 'Solid Hub'],
  ['/settings/ecosystem/directory', 'Directory'],
  ['/settings/ecosystem/sales', 'Solid Sales'],
];

/** Off-nav screens under an id, which a prefix cannot name (`/agreements/:id/report`). */
const OFF_NAV_PATTERNS: Array<[pattern: RegExp, label: string]> = [
  [/^\/agreements\/[^/]+\/report\/?$/, 'Hours report'],
];

/**
 * ⚠ The OFF-NAV table is consulted FIRST — most specific wins. A nav item
 * matches on prefix, so `/settings/…` would otherwise always resolve to
 * "Settings" and a sub-route could never name itself, which is the same
 * anonymity the table exists to prevent.
 */
export function pageTitleFor(pathname: string): string {
  const patterned = OFF_NAV_PATTERNS.find(([re]) => re.test(pathname));
  if (patterned) return patterned[1];
  const off = OFF_NAV_TITLES.find(([p]) => pathname === p || pathname.startsWith(`${p}/`));
  if (off) return off[1];
  const item = NAV_ITEMS.find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
  return item?.label ?? 'Solid Financials';
}
